import React, { useState, useCallback, useEffect } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronRight, GripVertical, Edit, Trash2 } from 'lucide-react'
import { ISectionComponent, FormComponentType, IFormControlChange } from '../df-form-preview-interfaces'

// Import form control components for rendering children
import { DfFormInput } from '../df-form-input'
import { DfFormTextarea } from '../df-form-textarea'
import { DfFormSelect } from '../df-form-select'
import { DfFormCheckbox } from '../df-form-checkbox'
import { DfFormRadio } from '../df-form-radio'
import { DfFormDateTime } from '../df-form-date-time'
import { DfFormSignature } from '../df-form-signature'
import { DfFormHeading } from '../df-form-heading'

interface DfFormSectionProps {
  id: string
  properties: ISectionComponent
  validationErrors?: Record<string, any>
  formValue?: any
  formData?: Record<string, any>
  readonly?: boolean
  disabled?: boolean
  touchedFields?: Record<string, boolean>
  formSubmitted?: boolean
  mode?: 'edit' | 'preview' | 'test'
  onValueChange?: (change: IFormControlChange) => void
  onBlur?: () => void
  onFocus?: () => void
  onSelect?: () => void
  isSelected?: boolean
  className?: string
  // Section-specific props
  onSectionSelect?: (section: ISectionComponent) => void
  onSectionDelete?: (sectionId: string) => void
  onChildrenChange?: (children: FormComponentType[]) => void
  onChildSelect?: (child: FormComponentType) => void
  onChildDelete?: (childId: string) => void
  selectedChild?: FormComponentType | null
  renderFormComponent?: (field: FormComponentType) => React.ReactNode
}

// Draggable child component for items inside the section
interface DraggableChildProps {
  child: FormComponentType
  selectedChild: FormComponentType | null
  mode: 'edit' | 'preview' | 'test'
  onChildSelect: (child: FormComponentType) => void
  onChildDelete: (child: FormComponentType, event: React.MouseEvent) => void
  renderFormComponent: (field: FormComponentType) => React.ReactNode
  isOverlay?: boolean
  isChildrenEditMode?: boolean
}

const DraggableChild: React.FC<DraggableChildProps> = ({
  child,
  selectedChild,
  mode,
  onChildSelect,
  onChildDelete,
  renderFormComponent,
  isOverlay = false,
  isChildrenEditMode = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: child.id,
    disabled: isOverlay || mode !== 'edit',
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`form-component section-child ${selectedChild?.id === child.id ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isSorting ? 'sorting' : ''} ${isChildrenEditMode ? 'children-edit-active' : ''}`}
      onClick={() => !isDragging && onChildSelect(child)}
      role="button"
      tabIndex={0}
    >
      {/* Drag Handle - Show in edit mode or when children edit mode is active */}
      {(mode === 'edit' || isChildrenEditMode) && (
        <div 
          className="child-drag-handle" 
          {...listeners} 
          {...attributes}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} />
        </div>
      )}
      
      {/* Child Content */}
      <div className="form-component-content section-child-content">
        {/* Child Actions - Show in edit mode or when children edit mode is active */}
        {(mode === 'edit' || isChildrenEditMode) && (
          <div className="component-actions child-actions">
            <button
              className="btn edit-btn"
              onClick={(e) => {
                e.stopPropagation()
                onChildSelect(child)
              }}
              onMouseDown={(e) => e.stopPropagation()}
              title="Edit properties"
            >
              <Edit size={12} />
            </button>
            <button
              className="btn delete-btn"
              onClick={(e) => onChildDelete(child, e)}
              onMouseDown={(e) => e.stopPropagation()}
              title="Delete component"
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}

        <div className="component-preview child-preview">
          {renderFormComponent(child)}
        </div>
      </div>
    </div>
  )
}

export const DfFormSection: React.FC<DfFormSectionProps> = ({
  id,
  properties,
  mode = 'edit',
  formData = {},
  onValueChange,
  onSelect,
  isSelected = false,
  className = '',
  onSectionSelect,
  onChildSelect,
  onChildDelete,
  selectedChild,
  renderFormComponent
}) => {
  const [isCollapsed, setIsCollapsed] = useState(properties.basic.collapsed)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [sectionTitle, setSectionTitle] = useState(properties.basic.label)
  const [sectionDescription, setSectionDescription] = useState(properties.basic.description)
  const [children, setChildren] = useState<FormComponentType[]>(properties.children || [])
  const [isChildrenEditMode, setIsChildrenEditMode] = useState(false)

  // Sync children with properties changes
  useEffect(() => {
    setChildren(properties.children || [])
  }, [properties.children])

  // Sync basic props (label, description) with incoming properties updates
  useEffect(() => {
    setSectionTitle(properties.basic.label)
    setSectionDescription(properties.basic.description)
  }, [properties.basic.label, properties.basic.description])

  // Make the section a drop target - Only in edit mode
  const { setNodeRef, isOver } = useDroppable({
    id: `section-${id}`,
    disabled: mode !== 'edit'
  })

  // Decide visibility after all hooks are called to keep hooks order stable
  const shouldHideInCurrentMode = (mode === 'preview' || mode === 'test') && (!(properties.children && properties.children.length > 0))

  // Handle section title change
  const handleTitleChange = useCallback((newTitle: string) => {
    setSectionTitle(newTitle)
    onValueChange?.({
      id,
      value: { ...properties, basic: { ...properties.basic, label: newTitle } }
    })
  }, [id, properties, onValueChange])

  // Handle section description change
  const handleDescriptionChange = useCallback((newDescription: string) => {
    setSectionDescription(newDescription)
    onValueChange?.({
      id,
      value: { ...properties, basic: { ...properties.basic, description: newDescription } }
    })
  }, [id, properties, onValueChange])

  // Handle child selection
  const handleChildSelect = useCallback((child: FormComponentType) => {
    onChildSelect?.(child)
  }, [onChildSelect])

  // Handle child deletion
  const handleChildDelete = useCallback((child: FormComponentType, event: React.MouseEvent) => {
    event.stopPropagation()
    
    // Update local children state immediately for responsive UI
    const newChildren = children.filter(c => c.id !== child.id)
    setChildren(newChildren)
    
    // Notify parent component - this will trigger the useEffect to sync
    onChildDelete?.(child.id)
  }, [onChildDelete, children, id])

  // Handle section selection
  const handleSectionSelect = useCallback(() => {
    onSectionSelect?.(properties)
    onSelect?.()
  }, [onSectionSelect, onSelect, properties])

  // Handle section double-click to activate children edit mode
  const handleSectionDoubleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (mode === 'edit' && children.length > 0) {
      setIsChildrenEditMode(!isChildrenEditMode)
    }
  }, [mode, children.length, isChildrenEditMode])

  // Toggle collapse state
  const toggleCollapse = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    setIsCollapsed(!isCollapsed)
    onValueChange?.({
      id,
      value: { ...properties, basic: { ...properties.basic, collapsed: !isCollapsed } }
    })
  }, [isCollapsed, id, properties, onValueChange])

  // Use the provided render function or fallback to default
  const renderComponent = renderFormComponent || useCallback((field: FormComponentType) => {
    const commonProps = {
      id: field.id,
      properties: field,
      validationErrors: {},
      formValue: mode === 'test' ? (formData[field.id] || ('defaultValue' in field.basic ? field.basic.defaultValue || '' : '')) : ('defaultValue' in field.basic ? field.basic.defaultValue || '' : ''),
      touchedFields: {},
      formSubmitted: false,
      mode,
      onValueChange: onValueChange, // Pass the parent's onValueChange to child components
      onBlur: () => {},
      onFocus: () => {}
    }

    switch (field.name) {
      case 'text-input':
      case 'email-input':
      case 'number-input':
        return (
          <DfFormInput
            {...commonProps}
            properties={field as any}
            inputType={field.name === 'text-input' ? 'text' : 
                      field.name === 'number-input' ? 'number' : 'email'}
            readonly={mode === 'edit' || mode === 'preview'}
            disabled={mode === 'preview'}
          />
        )
      
      case 'textarea':
        return <DfFormTextarea {...commonProps} properties={field as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} />
      
      case 'select':
        // Map basic.options to top-level options for select component
        const selectProps = {
          ...field,
          options: (field as any).basic?.options || []
        }
        return <DfFormSelect {...commonProps} properties={selectProps as any} disabled={mode === 'edit' || mode === 'preview'} />
      
      case 'checkbox':
        // Map basic.options to top-level options for checkbox component
        const checkboxProps = {
          ...field,
          options: (field as any).basic?.options || []
        }
        return <DfFormCheckbox {...commonProps} properties={checkboxProps as any} formValue={[]} disabled={mode === 'edit' || mode === 'preview'} />
      
      case 'radio':
        // Map basic.options to top-level options for radio component
        const radioProps = {
          ...field,
          options: (field as any).basic?.options || []
        }
        return <DfFormRadio {...commonProps} properties={radioProps as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'edit' || mode === 'preview'} />
      
      case 'date':
      case 'date-picker':
        return <DfFormDateTime {...commonProps} properties={field as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} />
      
      case 'datetime-picker':
        return <DfFormDateTime {...commonProps} properties={field as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} />
      
      case 'signature':
        return <DfFormSignature {...commonProps} properties={field as any} />
      
      case 'heading':
        return <DfFormHeading {...commonProps} properties={field as any} />
      
      default:
        return (
          <div className="form-group">
            <div className="form-group-label">
              Unsupported Component: {field.name}
            </div>
          </div>
        )
    }
  }, [mode, onValueChange, formData])

  if (shouldHideInCurrentMode) {
    return null
  }

  // Filter out dimension-related styles for sections as they should be flexible containers
  const sectionStyles = {
    backgroundColor: properties.styles.backgroundColor,
    borderColor: properties.styles.borderColor,
    borderWidth: properties.styles.borderWidth,
    borderRadius: properties.styles.borderRadius,
    padding: properties.styles.padding,
    margin: properties.styles.margin,
    // Explicitly exclude dimension properties
    // width, height, minWidth, maxWidth, minHeight, maxHeight should not be applied
  };

  return (
    <div 
      ref={setNodeRef}
      className={`df-form-section ${isSelected ? 'selected' : ''} ${isOver ? 'drag-over' : ''} ${className}`}
      style={sectionStyles}
    >
      {/* Section Header */}
      <div 
        className={`section-header ${isChildrenEditMode ? 'children-edit-mode' : ''}`}
        onClick={handleSectionSelect}
        onDoubleClick={handleSectionDoubleClick}
      >
        <div className="section-header-content">
          {/* Section Title with inline collapse icon */}
          <div className="section-title">
            {/* Left side: collapse icon in all modes */}
            <span
              className="section-toggle"
              title={isCollapsed ? 'Expand section' : 'Collapse section'}
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); toggleCollapse(e) }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapse(e as any) } }}
              style={{ marginRight: '8px', display: 'inline-flex', alignItems: 'center' }}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
            </span>

            {mode === 'edit' && isEditingTitle ? (
              <input
                type="text"
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                onBlur={() => {
                  setIsEditingTitle(false)
                  handleTitleChange(sectionTitle)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingTitle(false)
                    handleTitleChange(sectionTitle)
                  }
                }}
                className="section-title-input"
                autoFocus
              />
            ) : (
              <h3 
                className="section-title-text"
                onDoubleClick={() => mode === 'edit' && setIsEditingTitle(true)}
                style={{ display: 'inline', margin: 0 }}
              >
                {sectionTitle || 'Untitled Section'}
              </h3>
            )}
          </div>

        </div>
 
        {/* Section Description (below header) */}
        {(sectionDescription || mode === 'edit') && (
          <div className="section-description">
            {mode === 'edit' && isEditingDescription ? (
              <textarea
                value={sectionDescription}
                onChange={(e) => setSectionDescription(e.target.value)}
                onBlur={() => {
                  setIsEditingDescription(false)
                  handleDescriptionChange(sectionDescription)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    setIsEditingDescription(false)
                    handleDescriptionChange(sectionDescription)
                  }
                }}
                className="section-description-input"
                placeholder="Section description (optional)"
                autoFocus
              />
            ) : (
              <p 
                className="section-description-text"
                onDoubleClick={() => mode === 'edit' && setIsEditingDescription(true)}
              >
                {sectionDescription || 'Section description'}
              </p>
            )}
          </div>
        )}

       
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div 
          className={`section-content ${isOver ? 'drag-over' : ''} ${children.length === 0 ? 'empty' : 'has-children'}`}
          onClick={(e) => {
            // Only select section if clicking on the background, not on child components
            if (e.target === e.currentTarget) {
              handleSectionSelect()
            }
          }}
        >
          <SortableContext items={children.map(c => c.id)} strategy={verticalListSortingStrategy}>
            {children.length === 0 ? (
              <div 
                className="section-empty-state" 
                onClick={handleSectionSelect}
                style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px',
                  padding: '40px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  border: '2px dotted #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'var(--df-color-fb-container)',
                  transition: 'all 0.3s ease',
                  margin: '16px 0'
                }}
              >
                {/* <div style={{ fontSize: '24px', opacity: 0.5 }}>📋</div> */}
                <div style={{ 
                  fontWeight: '500',
                  color: isOver ? '#3b82f6' : '#374151'
                }}>
                  {isOver ? 'Drop components here' : 'Empty Section'}
                </div>
                <div style={{ 
                  fontSize: '12px',
                  color: '#9ca3af'
                }}>
                  Drag and drop components here to create your section
                </div>
              </div>
            ) : (
              children.map((child) => (
                <DraggableChild
                  key={child.id}
                  child={child}
                  selectedChild={selectedChild || null}
                  mode={mode}
                  onChildSelect={handleChildSelect}
                  onChildDelete={handleChildDelete}
                  renderFormComponent={renderComponent}
                  isChildrenEditMode={isChildrenEditMode}
                />
              ))
            )}
          </SortableContext>
        </div>
      )}
    </div>
  )
}

export default DfFormSection
